import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DocumentType, PurchaseOrder, PurchaseOrderDocument, purchaseOrdersAPI } from '../../services/api';
import {
    ArrowLeftIcon,
    ArrowDownTrayIcon,
    PencilIcon,
    CheckCircleIcon,
    DocumentArrowUpIcon,
    DocumentTextIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Badge, Spinner } from '../ui';

const STATUS_CONFIG: Record<string, { label: string; variant: 'neutral' | 'warning' | 'primary' | 'success'; next?: string; nextLabel?: string }> = {
    CREATED: { label: 'Created', variant: 'neutral', next: 'SENT', nextLabel: 'Mark as Sent' },
    SENT: { label: 'Sent', variant: 'warning', next: 'CONFIRMED', nextLabel: 'Mark as Confirmed' },
    CONFIRMED: { label: 'Confirmed', variant: 'primary', next: 'RECEIVED', nextLabel: 'Mark as Received' },
    RECEIVED: { label: 'Received', variant: 'success' },
};

const FLOW = ['CREATED', 'SENT', 'CONFIRMED', 'RECEIVED'];

const PurchaseOrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [po, setPo] = useState<PurchaseOrder | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [advancing, setAdvancing] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [documents, setDocuments] = useState<PurchaseOrderDocument[]>([]);
    const [docFile, setDocFile] = useState<File | null>(null);
    const [docName, setDocName] = useState('');
    const [docDescription, setDocDescription] = useState('');
    const [docType, setDocType] = useState<DocumentType>('OTHER');
    const [uploadingDoc, setUploadingDoc] = useState(false);

    const fetchPO = async () => {
        try {
            setLoading(true);
            const data = await purchaseOrdersAPI.getById(+id!);
            setPo(data);
            setDocuments(data.documents || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load purchase order');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPO(); }, [id]);

    const handleAdvanceStatus = async () => {
        if (!po) return;
        const sc = STATUS_CONFIG[po.status];
        if (!sc.next) return;

        setAdvancing(true);
        try {
            const updated = await purchaseOrdersAPI.updateStatus(po.id, sc.next);
            setPo(updated);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update status');
        } finally {
            setAdvancing(false);
        }
    };

    const fetchDocuments = async () => {
        if (!po) return;
        try {
            const data = await purchaseOrdersAPI.getDocuments(po.id);
            setDocuments(data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load documents');
        }
    };

    const handleUploadDocument = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!po || !docFile) return;

        try {
            setUploadingDoc(true);
            await purchaseOrdersAPI.uploadDocument(po.id, docFile, {
                documentType: docType,
                name: docName.trim() || docFile.name,
                description: docDescription.trim() || undefined,
            });
            setDocFile(null);
            setDocName('');
            setDocDescription('');
            setDocType('OTHER');
            await fetchDocuments();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to upload document');
        } finally {
            setUploadingDoc(false);
        }
    };

    const handleDeleteDocument = async (documentId: number) => {
        if (!po || !window.confirm('Delete this purchase order document?')) return;
        try {
            await purchaseOrdersAPI.deleteDocument(po.id, documentId);
            await fetchDocuments();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete document');
        }
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return '-';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleDownloadPdf = async () => {
        if (!po) return;
        setDownloading(true);
        try {
            const blob = await purchaseOrdersAPI.downloadPdf(po.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${po.poNumber}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center h-64"><Spinner size="lg" /></div>;
    if (!po) return <div className="px-8 py-8 text-center text-error-600">{error || 'Not found'}</div>;

    const currentStatusConfig = STATUS_CONFIG[po.status] || STATUS_CONFIG.CREATED;
    const currentFlowIndex = FLOW.indexOf(po.status);

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <button onClick={() => navigate('/admin/purchase-orders')} className="flex items-center text-sm text-neutral-600 hover:text-neutral-900 mb-4">
                    <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Purchase Orders
                </button>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <div className="flex items-center space-x-3">
                            <h1 className="text-2xl font-bold text-neutral-900">{po.poNumber}</h1>
                            <Badge variant={currentStatusConfig.variant} size="md">{currentStatusConfig.label}</Badge>
                        </div>
                        <p className="text-sm text-neutral-500 mt-1">Created {new Date(po.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center space-x-2 flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading}
                            leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}>
                            {downloading ? 'Downloading...' : 'Download PDF'}
                        </Button>
                        {po.status !== 'RECEIVED' && (
                            <Link to={`/admin/purchase-orders/${po.id}/edit`}>
                                <Button variant="outline" size="sm" leftIcon={<PencilIcon className="h-4 w-4" />}>Edit</Button>
                            </Link>
                        )}
                        {currentStatusConfig.next && (
                            <Button variant="primary" size="sm" onClick={handleAdvanceStatus} disabled={advancing}
                                leftIcon={<CheckCircleIcon className="h-4 w-4" />}>
                                {advancing ? 'Updating...' : currentStatusConfig.nextLabel}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <Card variant="bordered" className="mb-4 border-error-200 bg-error-50">
                    <CardBody className="p-4"><div className="text-sm text-error-700">{error}</div></CardBody>
                </Card>
            )}

            {/* Status Timeline */}
            <Card variant="elevated" className="mb-6">
                <CardBody className="p-6">
                    <h2 className="text-sm font-semibold text-neutral-700 mb-4">Order Status</h2>
                    <div className="flex items-center justify-between">
                        {FLOW.map((step, index) => {
                            const sc = STATUS_CONFIG[step];
                            const isActive = index <= currentFlowIndex;
                            const isCurrent = step === po.status;
                            return (
                                <React.Fragment key={step}>
                                    <div className="flex flex-col items-center">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isCurrent ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                                                : isActive ? 'bg-success-500 text-white'
                                                    : 'bg-neutral-200 text-neutral-500'
                                            }`}>
                                            {isActive && !isCurrent ? '✓' : index + 1}
                                        </div>
                                        <span className={`text-xs mt-2 font-medium ${isCurrent ? 'text-primary-600' : isActive ? 'text-success-600' : 'text-neutral-400'}`}>
                                            {sc.label}
                                        </span>
                                    </div>
                                    {index < FLOW.length - 1 && (
                                        <div className={`flex-1 h-1 mx-2 rounded ${index < currentFlowIndex ? 'bg-success-400' : 'bg-neutral-200'}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </CardBody>
            </Card>

            {/* Company & Supplier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card variant="elevated">
                    <CardBody className="p-6">
                        <h2 className="text-sm font-semibold text-neutral-700 mb-3">From (Company)</h2>
                        <p className="text-lg font-bold text-neutral-900">{po.company?.name}</p>
                        {po.company?.address && <p className="text-sm text-neutral-600 mt-1">{po.company.address}</p>}
                        {(po.company?.city || po.company?.state) && <p className="text-sm text-neutral-600">{[po.company.city, po.company.state].filter(Boolean).join(', ')}</p>}
                        {po.company?.country && <p className="text-sm text-neutral-600">{po.company.country}</p>}
                        {po.company?.phone && <p className="text-sm text-neutral-500 mt-2">📞 {po.company.phone}</p>}
                        {po.company?.email && <p className="text-sm text-neutral-500">✉️ {po.company.email}</p>}
                    </CardBody>
                </Card>
                <Card variant="elevated">
                    <CardBody className="p-6">
                        <h2 className="text-sm font-semibold text-neutral-700 mb-3">To (Supplier)</h2>
                        <p className="text-lg font-bold text-neutral-900">{po.supplier?.name}</p>
                        {po.supplier?.address && <p className="text-sm text-neutral-600 mt-1">{po.supplier.address}</p>}
                        {po.supplier?.emails?.[0] && <p className="text-sm text-neutral-500 mt-2">✉️ {po.supplier.emails[0]}</p>}
                        {po.supplier?.phoneNumbers?.[0] && <p className="text-sm text-neutral-500">📞 {po.supplier.phoneNumbers[0]}</p>}
                    </CardBody>
                </Card>
            </div>

            {/* Order Info */}
            <Card variant="elevated" className="mb-6">
                <CardBody className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <p className="text-xs font-medium text-neutral-500 uppercase">Expected Delivery</p>
                            <p className="text-sm font-semibold text-neutral-900 mt-1">
                                {po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate).toLocaleDateString() : 'Not set'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-neutral-500 uppercase">Currency</p>
                            <p className="text-sm font-semibold text-neutral-900 mt-1">{po.currency}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-neutral-500 uppercase">Items</p>
                            <p className="text-sm font-semibold text-neutral-900 mt-1">{po.items?.length || 0}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-neutral-500 uppercase">Total Amount</p>
                            <p className="text-lg font-bold text-primary-600 mt-1">{po.currency} {Number(po.totalAmount).toFixed(2)}</p>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Items Table */}
            <Card variant="elevated" className="mb-6 overflow-hidden">
                <CardBody className="p-0">
                    <div className="px-6 py-4 border-b border-neutral-200">
                        <h2 className="text-sm font-semibold text-neutral-700">Line Items</h2>
                    </div>
                    <table className="min-w-full divide-y divide-neutral-200">
                        <thead className="bg-neutral-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">#</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Description</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Product</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Qty</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Unit Price</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Total</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-neutral-200">
                            {po.items?.map((item, index) => (
                                <tr key={item.id}>
                                    <td className="px-6 py-4 text-sm text-neutral-500">{index + 1}</td>
                                    <td className="px-6 py-4 text-sm text-neutral-900 font-medium">{item.description}</td>
                                    <td className="px-6 py-4 text-sm text-neutral-500">{item.product ? `${item.product.sku} - ${item.product.name}` : '-'}</td>
                                    <td className="px-6 py-4 text-sm text-neutral-900 text-right">{item.quantity}</td>
                                    <td className="px-6 py-4 text-sm text-neutral-900 text-right">{Number(item.unitPrice).toFixed(2)}</td>
                                    <td className="px-6 py-4 text-sm font-medium text-neutral-900 text-right">{Number(item.totalPrice).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-neutral-50">
                                <td colSpan={5} className="px-6 py-4 text-sm font-bold text-neutral-900 text-right">Grand Total</td>
                                <td className="px-6 py-4 text-lg font-bold text-primary-600 text-right">{po.currency} {Number(po.totalAmount).toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </CardBody>
            </Card>

            <Card variant="elevated" className="mb-6 overflow-hidden">
                <CardBody className="p-0">
                    <div className="border-b border-neutral-200 px-6 py-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-neutral-700">Documents</h2>
                                <p className="mt-1 text-sm text-neutral-500">Files attached to this supplier purchase order.</p>
                            </div>
                            <Badge variant="neutral">{documents.length} files</Badge>
                        </div>
                    </div>

                    <div className="border-b border-neutral-200 bg-neutral-50 p-6">
                        <form onSubmit={handleUploadDocument} className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                            <div className="lg:col-span-3">
                                <label className="block text-xs font-medium text-neutral-600">Document Type</label>
                                <select
                                    value={docType}
                                    onChange={(event) => setDocType(event.target.value as DocumentType)}
                                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                >
                                    <option value="PURCHASE_ORDER">Purchase Order</option>
                                    <option value="INVOICE">Invoice</option>
                                    <option value="CONTRACT">Contract</option>
                                    <option value="OTHER">Other</option>
                                </select>
                            </div>
                            <div className="lg:col-span-3">
                                <label className="block text-xs font-medium text-neutral-600">Name</label>
                                <input
                                    value={docName}
                                    onChange={(event) => setDocName(event.target.value)}
                                    placeholder="Defaults to file name"
                                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                />
                            </div>
                            <div className="lg:col-span-3">
                                <label className="block text-xs font-medium text-neutral-600">File *</label>
                                <input
                                    type="file"
                                    onChange={(event) => setDocFile(event.target.files?.[0] || null)}
                                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.xls"
                                    className="mt-1 block w-full text-sm text-neutral-500 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
                                />
                            </div>
                            <div className="lg:col-span-2">
                                <label className="block text-xs font-medium text-neutral-600">Description</label>
                                <input
                                    value={docDescription}
                                    onChange={(event) => setDocDescription(event.target.value)}
                                    className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                />
                            </div>
                            <div className="flex items-end lg:col-span-1">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="sm"
                                    disabled={!docFile || uploadingDoc}
                                    isLoading={uploadingDoc}
                                    leftIcon={<DocumentArrowUpIcon className="h-4 w-4" />}
                                >
                                    Upload
                                </Button>
                            </div>
                        </form>
                    </div>

                    {documents.length === 0 ? (
                        <div className="p-6 text-sm text-neutral-500">No documents uploaded.</div>
                    ) : (
                        <table className="min-w-full divide-y divide-neutral-200">
                            <thead className="bg-neutral-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-neutral-500">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-neutral-500">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-neutral-500">Uploaded</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-neutral-500">Size</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase text-neutral-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-200 bg-white">
                                {documents.map((document) => (
                                    <tr key={document.id}>
                                        <td className="px-6 py-4 text-sm font-medium text-neutral-900">
                                            {document.name}
                                            {document.description && <div className="mt-1 text-xs font-normal text-neutral-500">{document.description}</div>}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-neutral-600">{document.documentType.replace(/_/g, ' ')}</td>
                                        <td className="px-6 py-4 text-sm text-neutral-600">{new Date(document.uploadedAt).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 text-sm text-neutral-600">{formatFileSize(document.fileSize)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => purchaseOrdersAPI.downloadDocument(po.id, document.id, document.name)}
                                                    className="rounded-md p-1.5 text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-800"
                                                    title="Download document"
                                                >
                                                    <ArrowDownTrayIcon className="h-5 w-5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteDocument(document.id)}
                                                    className="rounded-md p-1.5 text-error-600 transition-colors hover:bg-error-50 hover:text-error-800"
                                                    title="Delete document"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Notes */}
            {po.notes && (
                <Card variant="elevated">
                    <CardBody className="p-6">
                        <h2 className="text-sm font-semibold text-neutral-700 mb-2">Notes</h2>
                        <p className="text-sm text-neutral-600 whitespace-pre-wrap">{po.notes}</p>
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default PurchaseOrderDetail;
